import { 
	GroupSharePermissions,
	GroupShareTriState,
	UserGroupPolicy,
	PhoenixUser
} from 'phoenix/domain';
import { 
	UserGroup,
	UserGroupShareRight, 
	UserGroupShareRightCollection,
} from 'sharebase/domain';
import { UserGroupShareRightCollectionFactory } from 'sharebase/factories/userGroupShareRightCollectionFactory';
		
/**
* @ngdoc service
* @name  sharebase.sbUserGroupDataStore
* 
* @description 
* A datastore used to retrieve and interact with a ShareBase user group
**/
export class UserGroupDataStore{
	public static $inject =  [
		'$q',
		'$timeout',
		'sbUserGroupService',
		'sbUserGroupShareRightCollectionFactory',
		'gettextCatalog'
	];
	
	private _userGroup:UserGroup;
	private _policy:UserGroupPolicy;

	public sharingRights:UserGroupShareRightCollection;

	constructor(
		private $q:ng.IQService, 
		private $timeout: ng.ITimeoutService, 
		private sbUserGroupService,
		private sbUserGroupShareRightCollectionFactory:UserGroupShareRightCollectionFactory,
		private gettextCatalog:ng.gettext.gettextCatalog
	) {
	}


	/**
	* @ngdoc method
	* @name sbUserGroupDataStore#containsUser
	* @methodOf sharebase.sbUserGroupDataStore
	* @kind function
	*
	* @description
	* Determines whether a user exists in the user group's collection of users
	*
	* @returns {boolean} A boolean represnetation of a user's existance in the user group's collection of users
	* 
	*/
	public containsUser(user){
		return this._userGroup.contains(user);
	}

	/**
	* @ngdoc property
	* @name sbUserGroupDataStore#userGroup
	* @propertyOf sharebase.sbUserGroupDataStore
	* @kind property
	*
	* @description
	* A user group
	* 
	* @returns {object} A user group
	*/
	public get userGroup() { 
		return this._userGroup; 
	}

	/**
	* @ngdoc property
	* @name sbUserGroupDataStore#userGroupPolicy
	* @propertyOf sharebase.sbUserGroupDataStore
	* @kind property
	*
	* @description
	* A user group
	* 
	* @returns {object} A user group policy
	*/
	public get userGroupPolicy() { 
		return this._policy; 
	}

	/**
	* @ngdoc method
	* @name sbUserGroupDataStore#getUserGroup
	* @methodOf sharebase.sbUserGroupDataStore
	* @kind function
	*
	* @description
	* Gets a user group
	*
	* @returns {Deferred} A promise that will resolve with a user group
	* 
	*/
	public getUserGroup(userGroupId:number, systemName:string) {
		var p;

		if (!userGroupId) {
			this._userGroup = new UserGroup();
			this._policy = null;
			p = this.$q.resolve(this._userGroup);
		}
		else {
			p = this.sbUserGroupService
				.getUserGroup(userGroupId, systemName)
				.then(response => this._userGroup = this.toGroup(response));
		}

		return p;
	}

	/**
	* @ngdoc method
	* @name sbUserGroupDataStore#getUserGroupPolicy
	* @methodOf sharebase.sbUserGroupDataStore
	* @kind function
	*
	* @description
	* Gets a user group's sharing policy
	*
	* @returns {Deferred} A promise that will resolve with a user group sharing policy
	* 
	*/
	public getUserGroupPolicy(userGroupId:number) {
		return this.sbUserGroupService
			.getUserGroupPolicy(userGroupId)
			.then((response:UserGroupPolicy) => {
				if (response.MaxLinkExpirationDays == 9223372036854775807) {
					response.MaxLinkExpirationDays = null;
				}
				
				this._policy = response;
				this.sharingRights = this.sbUserGroupShareRightCollectionFactory.create(this._policy.AllowPrivileges, this._policy.DenyPrivileges);

				return this._policy;
			});
	}

	/**
	* @ngdoc method
	* @name sbUserGroupDataStore#saveUserGroup
	* @methodOf sharebase.sbUserGroupDataStore
	* @kind function
	*
	* @description
	* Saves a UserGroup
	*
	* @returns {Deferred} A promise that will resolve with a saved user group
	* 
	*/
	public saveUserGroup(systemName:string) {
		if(this._userGroup.isSystemGroup) {
			return this.$q.reject(new Error('System groups cannot be edited'));
		}

		if (this._userGroup.id) {
			return this.sbUserGroupService
				.updateUserGroup(this._userGroup.toServer(), systemName)
				.then(response => this._userGroup = this.toGroup(response));
		}
		else {
			return this.sbUserGroupService
				.addUserGroup(this._userGroup.toServer(), systemName)
				.then(response => this._userGroup = this.toGroup(response));
		}
	}

	/**
	* @ngdoc method
	* @name sbUserGroupDataStore#saveUserGroupPolicy
	* @methodOf sharebase.sbUserGroupDataStore
	* @kind function
	*
	* @description
	* Saves a UserGroup Policy
	*
	* @returns {Deferred} A promise that will resolve with a saved user group policy
	* 
	*/
	public saveUserGroupPolicy() {
		this._policy.UserGroupId = this._policy.UserGroupId || this._userGroup.id;
		if (this._policy.MaxLinkExpirationDays === null) {
			this._policy.MaxLinkExpirationDays = '9223372036854775807';
		}

		let permissions = this.sharingRights.generatePermissions();
		this._policy.AllowPrivileges = permissions.allow;
		this._policy.DenyPrivileges = permissions.deny;
		
		return this.sbUserGroupService
			.updateUserGroupPolicy(this._policy)
			.then(response => this._policy = response);
	}

	private toGroup(resp) {
		return new UserGroup(resp.UserGroupId, resp.UserGroupName, resp.Embedded.Users, resp.IsSystemGroup);
	}

}


// WEBPACK FOOTER //
// ./src/sharebase/dataStores/userGroupDataStore.ts